(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.QCatalogSearchData = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function create(options) {
    options = options || {};
    var fetcher = options.fetch || globalThis.fetch;
    var routeRoot = String(options.routeRoot || '/').replace(/\/?$/, '/');
    var limit = Number(options.productLimit || 248);
    var sleep = options.sleep || function (ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); };
    var products = new Map();
    var metadata = new Map();
    var active = 0;
    var queue = [];
    var disposed = false;
    var controllers = new Set();

    function key(handle, page) { return handle + ':' + page; }
    function forget(handle, page) {
      if (!page) {
        Array.from(products.keys()).filter(function (item) { return item.startsWith(handle + ':'); }).forEach(function (item) { products.delete(item); });
        Array.from(metadata.keys()).filter(function (item) { return item.startsWith(handle + ':'); }).forEach(function (item) { metadata.delete(item); });
        return;
      }
      products.delete(key(handle, page));
      var first = Math.ceil((((page - 1) * limit) + 1) / 250);
      var last = Math.ceil((page * limit) / 250);
      for (var i = first; i <= last; i++) metadata.delete(key(handle, i));
    }
    function pump() {
      while (!disposed && active < 3 && queue.length) {
        var task = queue.shift();
        active++;
        task().finally(function () { active--; pump(); });
      }
    }
    function slot(task) {
      return new Promise(function (resolve, reject) {
        queue.push(function () { return Promise.resolve().then(task).then(resolve, reject); });
        pump();
      });
    }
    async function json(url, acceptJson) {
      for (var attempt = 0; attempt < 2; attempt++) {
        if (disposed) throw new Error('Catalog source disposed');
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        if (controller) controllers.add(controller);
        var response;
        try {
          var requestOptions = {};
          if (controller) requestOptions.signal = controller.signal;
          if (acceptJson) requestOptions.headers = { Accept: 'application/json' };
          response = await fetcher(url, requestOptions);
          if (response.status === 429 && attempt === 0) {
            var raw = response.headers && response.headers.get && response.headers.get('Retry-After');
            var seconds = raw == null || raw === '' ? 1 : Number(raw);
            if (!Number.isFinite(seconds)) seconds = Math.max(0, (Date.parse(raw) - Date.now()) / 1000);
            if (!Number.isFinite(seconds) || seconds > 10) throw new Error('Catalog rate limit; retry manually');
            await sleep(Math.max(0, seconds) * 1000);
            continue;
          }
          if (!response.ok) throw new Error('Catalog HTTP ' + response.status);
          var data = await response.json();
          if (!data || !Array.isArray(data.products)) throw new Error('Invalid catalog products array');
          return data.products;
        } finally { if (controller) controllers.delete(controller); }
      }
      throw new Error('Catalog rate limit; retry manually');
    }
    function metadataPage(handle, page) {
      var cacheKey = key(handle, page);
      if (metadata.has(cacheKey)) return metadata.get(cacheKey);
      var params = new URLSearchParams({ view: 'q-single-data' });
      if (page > 1) params.set('page', String(page));
      var request = json(routeRoot + 'collections/' + encodeURIComponent(handle) + '?' + params.toString(), false).then(function (rows) {
        var map = new Map();
        rows.forEach(function (row) {
          if (!row || !row.handle || !row.metafields || typeof row.metafields !== 'object' || Array.isArray(row.metafields) || map.has(row.handle)) throw new Error('Invalid catalog metadata');
          map.set(row.handle, row.metafields);
        });
        return map;
      }).catch(function (error) { metadata.delete(cacheKey); throw error; });
      metadata.set(cacheKey, request);
      return request;
    }
    function page(handle, pageNumber) {
      var number = Number(pageNumber);
      if (!handle || !Number.isSafeInteger(number) || number < 1) return Promise.reject(new Error('Invalid catalog page'));
      if (disposed) return Promise.reject(new Error('Catalog source disposed'));
      var cacheKey = key(handle, number);
      if (products.has(cacheKey)) return products.get(cacheKey);
      var request = slot(async function () {
        var first = Math.ceil((((number - 1) * limit) + 1) / 250);
        var last = Math.ceil((number * limit) / 250);
        var metaPromises = [];
        for (var i = first; i <= last; i++) metaPromises.push(metadataPage(handle, i));
        var params = new URLSearchParams({ limit: String(limit) });
        if (number > 1) params.set('page', String(number));
        var url = routeRoot + 'collections/' + encodeURIComponent(handle) + '/products.json?' + params.toString();
        var settled = await Promise.allSettled([json(url, true), Promise.allSettled(metaPromises)]);
        if (settled[0].status === 'rejected') throw settled[0].reason;
        var rows = settled[0].value;
        if (rows.length > limit) throw new Error('Oversized catalog page');
        if (!rows.length) return { page: number, products: [], reachedEnd: true };
        var neededLast = Math.ceil(((((number - 1) * limit) + rows.length)) / 250);
        var maps = [];
        for (var required = first; required <= neededLast; required++) {
          var metaResult = settled[1].value[required - first];
          if (metaResult.status === 'rejected') throw metaResult.reason;
          maps.push(metaResult.value);
        }
        var seenIds = new Set(); var seenHandles = new Set();
        var merged = rows.map(function (row) {
          if (!row || !row.id || !row.handle || seenIds.has(String(row.id)) || seenHandles.has(row.handle)) throw new Error('Duplicate or missing catalog identity');
          seenIds.add(String(row.id)); seenHandles.add(row.handle);
          var matched = maps.find(function (map) { return map.has(row.handle); });
          if (!matched) throw new Error('Missing catalog metadata for ' + row.handle);
          return Object.assign({}, row, { q_metafields: Object.assign({}, row.q_metafields || {}, matched.get(row.handle)) });
        });
        return { page: number, products: merged, reachedEnd: merged.length < limit };
      }).catch(function (error) { forget(handle, number); throw error; });
      products.set(cacheKey, request);
      return request;
    }
    function seed(handle, pages) {
      if (!Array.isArray(pages)) return;
      pages.forEach(function (entry) {
        if (entry && Number.isSafeInteger(entry.page) && Array.isArray(entry.products)) products.set(key(handle, entry.page), Promise.resolve(entry));
      });
    }
    function dispose() {
      disposed = true;
      controllers.forEach(function (controller) { controller.abort(); });
      queue.splice(0).forEach(function (task) { task(); });
      products.clear(); metadata.clear();
    }
    return { page: page, forget: forget, seed: seed, dispose: dispose, productLimit: limit };
  }
  return { create: create };
});
